import React, { useState, useEffect, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { getItem } from "../../../../../utils/localStorage";
import { toastify } from "../../../../../utils/toast";
import {
  getPendingWithdrawalsURL,
  approveWalletWithdrawalURL,
  rejectWalletWithdrawalURL
} from "../../../../../utils/urls";

const LandlordApprovalsTab = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  // Reject confirmation dialog state
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  const formatCurrency = (amount) => {
    if (typeof amount !== "number") return "KES 0.00";
    return `KES ${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId) return;

      const res = await makeRequest2(`${getPendingWithdrawalsURL}/${facilityId}`, "GET", {});

      if (res.success) {
        const rows = res.data?.data || res.data || [];
        setPending(Array.isArray(rows) ? rows : []);
      }
    } catch (err) {
      console.error("Failed to fetch pending withdrawals:", err);
      toastify(err.message || "Failed to fetch pending withdrawals", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
  }, []);

  const handleApprove = async (row) => {
    try {
      setProcessingId(row._id);
      const facilityId = await getItem("selectedFacilityId");

      const response = await makeRequest2(
        `${approveWalletWithdrawalURL}/${facilityId}/${row._id}`,
        "POST",
        {}
      );

      if (response.success) {
        toastify("Withdrawal approved", "success");
      } else {
        throw new Error(response.error || response.message || "Failed to approve withdrawal");
      }
    } catch (err) {
      console.error("Approve withdrawal error:", err);
      toastify(err.message || "Failed to approve withdrawal", "error");
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectDialog = (row) => {
    setRejectTarget(row);
    setRejectReason("");
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    try {
      setRejectSubmitting(true);
      const facilityId = await getItem("selectedFacilityId");

      const res = await makeRequest2(
        `${rejectWalletWithdrawalURL}/${facilityId}/${rejectTarget._id}`,
        "POST",
        { reason: rejectReason }
      );

      if (res.success) {
        toastify("Withdrawal rejected", "success");
        setRejectTarget(null);
      } else {
        throw new Error(res.error || res.message || "Failed to reject withdrawal");
      }
    } catch (err) {
      console.error("Reject withdrawal error:", err);
      toastify(err.message || "Failed to reject withdrawal", "error");
    } finally {
      setRejectSubmitting(false);
    }
  };

  // Column templates
  const customerBodyTemplate = (row) => (
    <span className="fw-semibold">{row.customerId}</span>
  );

  const amountBodyTemplate = (row) => (
    <span className="fw-semibold text-danger">{formatCurrency(row.amount)}</span>
  );

  const dateBodyTemplate = (row) => {
    if (!row.createdAt) return "N/A";
    try {
      return new Date(row.createdAt).toLocaleString();
    } catch {
      return "Invalid Date";
    }
  };

  const notesBodyTemplate = (row) => (
    <span className="text-muted small">{row.notes || "—"}</span>
  );

  const actionsBodyTemplate = (row) => {
    const isProcessing = processingId === row._id;

    // Don't allow actions once processed
    if (row.status !== "pending") {
      return (
        <span
          className={`badge ${row.status === "completed"
              ? "bg-success"
              : "bg-danger"
            }`}
        >
          {row.status === "completed" ? "Approved" : "Rejected"}
        </span>
      );
    }

    return (
      <div className="d-flex gap-2">
        <button
          className="btn btn-sm btn-success"
          onClick={() => handleApprove(row)}
          disabled={isProcessing}
        >
          Approve
        </button>

        <button
          className="btn btn-sm btn-outline-danger"
          onClick={() => openRejectDialog(row)}
          disabled={isProcessing}
        >
          Reject
        </button>
      </div>
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h6 className="fw-bold mb-0">
          <i className="ti ti-clock-hour-4 text-warning me-2"></i>
          Pending Withdrawal Requests
        </h6>
      </div>

      <DataTable
        value={pending}
        loading={loading}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        emptyMessage="No pending withdrawal requests"
        sortField="createdAt"
        sortOrder={-1}
        className="p-datatable-sm"
        responsiveLayout="scroll"
      >
        <Column field="createdAt" header="Requested" body={dateBodyTemplate} sortable style={{ minWidth: "150px" }} />
        <Column field="customerId" header="Homeowner" body={customerBodyTemplate} style={{ minWidth: "180px" }} />
        <Column field="amount" header="Amount" body={amountBodyTemplate} sortable style={{ minWidth: "130px" }} />
        <Column field="notes" header="Notes" body={notesBodyTemplate} style={{ minWidth: "200px" }} />
        <Column header="Actions" body={actionsBodyTemplate} style={{ minWidth: "180px" }} />
      </DataTable>

      {/* Reject confirmation dialog */}
      <Dialog
        header="Reject Withdrawal Request"
        visible={!!rejectTarget}
        style={{ width: "420px" }}
        onHide={() => setRejectTarget(null)}
      >
        {rejectTarget && (
          <>
            <p className="text-muted small mb-3">
              You're rejecting a withdrawal request for{" "}
              <span className="fw-semibold">{formatCurrency(rejectTarget.amount)}</span>.
              This does not affect the wallet balance since funds were never deducted.
            </p>

            <div className="mb-3">
              <label className="form-label small fw-semibold">Reason (optional)</label>
              <InputTextarea
                className="form-control"
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Insufficient supporting documentation"
              />
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setRejectTarget(null)}
                disabled={rejectSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleReject}
                disabled={rejectSubmitting}
              >
                {rejectSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Processing...
                  </>
                ) : (
                  "Confirm Rejection"
                )}
              </button>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default LandlordApprovalsTab;